import { makeAutoObservable, runInAction } from 'mobx'
import get from 'lodash/get'
import feelGreateAPI from 'GlobalStores/FeelGreat/APIs'
import { ObjectHelpers } from 'Helpers/ObjectHelpers'
import StoreAuth from 'GlobalStores/User/StoreAuth'
import Customer from 'GlobalStores/User/Customer'
import { Customers } from 'Services/Cores/Customers/Customers'

class StoreReferral {
    referralData = null
    referralCode = ''
    loadingReferCode = false
    currentReferralData = null
    isOpenLoginModal = false
    instance = null
    referralCheckError = ''
    checkedReferralParam = false
    referralHistory = null
    useReferral = false
    subscriptionConfigData = null

    constructor() {
        makeAutoObservable(this)
    }
    /**
     * @returns {Customer} Customer
     */
    Customer() {
        if (!this.instance) {
            this.instance = new Customer(this.referralData)
        }
        return this.instance
    }

    // Replaces the referral rather than merging onto it. Opening a second referral link in the same
    // session used to leave whatever the new referrer's response did not mention behind - a display name
    // from one referrer next to the baId of another. Both callers pass a complete referrer.
    // The cached Customer has to go with it: it is built once from referralData, and CreateUuid reads
    // referral_id off it, so a stale instance sends the previous referrer's id with the new one's name.
    SetReferralData(userRef, status) {
        runInAction(() => {
            this.instance = null
            this.referralData = {
                ...userRef,
                shortStatus: status
            }
            this.referralHistory = {
                ...userRef,
                shortStatus: status
            }
            sessionStorage.setItem('referralHistory', JSON.stringify(this.referralHistory))
        })
    }

    // The referral only ever reached this store through the ReferralBar on the landing page, so a
    // reload straight into checkout lost it: HasReferral() went false, the cart fell back to retail
    // prices and the summary disagreed with the server, which had already priced the order with the
    // referral discount. detail-v2 still carries referral_id, so rebuild from the same endpoint the
    // ReferralBar uses, with the same item shape (Customer reads id.unicity).
    async RestoreFromReferralId(referralId) {
        if (!referralId || this.HasReferral()) {
            return false
        }

        try {
            const { data } = await Customers.getAllowReferrer(referralId)
            const baId = get(data, 'baId', '')

            if (!get(data, 'isAllow') || !baId) {
                return false
            }

            this.SetReferralData({ ...data, unicity: baId, id: { unicity: baId } }, get(data, 'baStatus', ''))

            return true
        } catch (error) {
            // A referrer we cannot confirm just means retail pricing; it must never stop the page
            // loading the way the publish 500 used to.
            return false
        }
    }

    async GetReferralCode(token) {
        try {
            this.loadingReferCode = true
            let response = await feelGreateAPI.getReferralCodes(token)
            this.referralCode = get(response, 'data.items[0].referralCode', '')
            this.loadingReferCode = false
        } catch (error) {
            console.log('getReferralCode error => ', error)
            this.loadingReferCode = false
        }
    }

    UsePrice() {
        if (StoreAuth.GetId()) {
            return 'after'
        } else if (get(this.referralData, 'id.unicity', '').length > 0) {
            return 'after'
        } else {
            return 'retail'
        }
    }

    GetReferral() {
        return this.referralData
    }

    // The displayName default matters: ObjectHelpers.isEmpty runs Object.keys, so a referrer that arrives
    // with a baId but no display name would throw here. Today only the falsy-baId short circuit hides it.
    // No name means nothing for the bar to show, so it counts as no referral.
    HasReferral() {
        const id = get(this.referralData, 'baId', '')

        return id && !ObjectHelpers.isEmpty(get(this.referralData, 'displayName', {})) ? id : false
    }

    HasReferralHistory() {
        const id = get(this.referralHistory, 'baId', '')

        return id && !ObjectHelpers.isEmpty(get(this.referralHistory, 'displayName', {})) ? id : false
    }

    SetReferralCheckError(msg) {
        this.referralCheckError = msg
    }

    GetReferralCheckError() {
        return this.referralCheckError
    }

    reset() {
        this.referralData = null
        this.instance = null
        this.useReferral = false
    }

    DisplayName() {
        return get(this.referralData, 'displayName', null)
    }

    SetCheckedReferralParam(value) {
        this.checkedReferralParam = value
    }

    GetCheckedReferralParam() {
        return this.checkedReferralParam
    }

    SetReferralHistory(value) {
        this.referralHistory = value
    }

    GetReferralHistory() {
        return this.referralHistory
    }

    SetSubscriptionConfigData(subscriptionConfigData) {
        if (subscriptionConfigData) {
            this.subscriptionConfigData = subscriptionConfigData
        }
    }

    GetSubscriptionConfigData(key) {
        if (key) {
            return get(this.subscriptionConfigData, key)
        } else {
            return this.subscriptionConfigData
        }
    }
}
const storeReferral = new StoreReferral()
export default storeReferral
